import React, { ReactNode, useEffect, useState } from 'react';
import cn from 'classnames';
import { BaseModal } from '@repo/components/modals';
import { UserSession } from '@repo/utils/types';
import { useAuthContext } from '@repo/hooks';
import { supportEmailProps } from '@repo/utils/helpers/preferred-email-client';
import styles from './DeleteAccountModal.module.scss';
import { capitalize } from '@repo/utils/helpers/string';

type AccountType = 'account' | 'workspace';
type ModalStep = 'initial' | 'confirm' | 'deleting' | 'error';

const headerCopy: { [k in ModalStep]: (accountType?: AccountType) => string } = {
  initial: (accountType) => `Are you sure you want to delete your ${accountType}?`,
  confirm: (accountType) => `${capitalize(accountType)} Deletion Confirmation`,
  deleting: (accountType) => `Deleting your ${accountType}`,
  error: () => 'Something went wrong',
};

const bodyCopy: { [k in ModalStep]: (accountType: AccountType, text?: string) => ReactNode } = {
  initial: (accountType) => `All your ${accountType} data will be permanently deleted.`,
  confirm: (accountType, text) => `To delete your ${accountType}, type ${text}.`,
  deleting: () => 'You will be automatically logged out in a moment.',
  error: (accountType) => (
    <>
      An error occurred when trying to delete your {accountType}. Please try again or{' '}
      <a {...supportEmailProps({ subject: `Problem deleting ${accountType}` })}>
        contact our customer support
      </a>
      .
    </>
  ),
};

const textForMatch = (valueType?: string, value?: string): string | undefined => {
  if (!valueType && !value) {
    return undefined;
  }

  if (valueType == 'email') {
    return `your email (${value})`;
  }

  if (valueType == 'name') {
    return `your name (${value})`;
  }

  return '"DELETE ACCOUNT"';
};

const valueToMatchForUser = (user: UserSession) => {
  if (user?.email) {
    return ['email', user.email];
  }

  if (user?.first_name && user?.last_name) {
    return ['name', `${user.first_name} ${user.last_name}`];
  }

  return ['fallback', 'DELETE ACCOUNT'];
};

interface DeleteAccountModalProps {
  accountType: 'account' | 'workspace';
  isOpen: boolean;
  closeModal: () => void;
  deleteAccount: () => void;
  isDeleting: boolean;
  error: boolean;
}

export default function DeleteAccountModal({
  accountType,
  isOpen,
  closeModal,
  deleteAccount,
  isDeleting,
  error,
}: DeleteAccountModalProps) {
  const { user } = useAuthContext();
  const [valueTypeToMatch, valueToMatch] = valueToMatchForUser(user);
  const [step, setStep] = useState<ModalStep>('initial');
  const [inputValue, setInputValue] = useState('');

  const cancel = () => {
    setInputValue('');
    if (!isDeleting && !error) {
      setStep('initial');
    }
    closeModal();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key == 'Enter' && inputValue == valueToMatch) {
      deleteAccount();
    }
  };

  const handleDeleteBtnClick = () => {
    if (step == 'initial') {
      setStep('confirm');
    } else if (step == 'confirm') {
      deleteAccount();
    }
  };

  useEffect(() => {
    if (isDeleting) {
      setStep('deleting');
    }
  }, [isDeleting]);

  useEffect(() => {
    if (!!error) {
      setStep('error');
    }
  }, [error]);

  return (
    <BaseModal
      isOpen={isOpen}
      overlayClassName="modal-confirm"
      className="modal-small"
      onRequestClose={cancel}
    >
      <div className="modal-header">{headerCopy[step](accountType)}</div>
      <div className="modal-content-padded">
        {bodyCopy[step](accountType, textForMatch(valueTypeToMatch, valueToMatch))}

        {step == 'confirm' && (
          <input
            type="text"
            className={cn(styles.input, 'form-input')}
            value={inputValue}
            onKeyDown={handleInputKeyDown}
            onChange={handleInputChange}
            placeholder={valueToMatch}
            autoFocus
          />
        )}

        <div className={styles.buttonRow}>
          <button className="btn btn-muted" type="button" onClick={cancel} disabled={isDeleting}>
            Cancel
          </button>
          {!error && (
            <button
              className="btn btn-danger"
              type="button"
              onClick={handleDeleteBtnClick}
              disabled={isDeleting || (step == 'confirm' && inputValue != valueToMatch)}
              autoFocus
            >
              Delete
            </button>
          )}
          {error && (
            <button className="btn btn-primary" type="button" onClick={deleteAccount}>
              Try Again
            </button>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
